import React, { JSX, useState } from "react";
import { useStripeModeFetch } from "../../../../global-hooks/default-system-hooks";
import {LinearProgress, CircularProgress, Button} from "@mui/material";
import { Link, useGetManyReference } from "react-admin";
import { useSubscriptionDataPanelLoader } from "./hooks/subscription-manager-active-subscription-data-panel.hooks";
import { SubscriptionManagerActiveSubscriptionInvoiceModal } from "./SubscriptionManagerActiveSubscriptionInvoiceModal";
import { SubscriptionInvoice } from "../../../../../types/entities";
import { convertCurrencyToSymbol } from "../../../../../defaults/defaults";


export const SubscriptionManagerActiveSubscriptionDataPanel: (props: any) => JSX.Element = (props: any): JSX.Element => {
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [activeInvoice, setActiveInvoice] = useState<SubscriptionInvoice>();
    const activeSubscription = props.activeSubscription;
    const { isTestMode, loadingTestModeData } = useStripeModeFetch();
    const { loadingData, subscriptionData, loadingError } = useSubscriptionDataPanelLoader(activeSubscription.id);
    const [activePage, setActivePage] = useState<number>(1);
    const currencyCode: string = props.activeCurrency;

    const INVOICES_PER_PAGE: number = 5;

    const { data, isPending, error, total } = useGetManyReference(
        'subscription_invoices',
        {
            target: "subscription",
            id: activeSubscription?.id,
            pagination: { page: activePage, perPage: INVOICES_PER_PAGE }
        },
    );

    // The page count was `total % 10`, so three invoices at five per page
    // reported "1/3" and the next-page guard let you walk past the end.
    const pageCount: number = Math.max(1, Math.ceil((total ?? 0) / INVOICES_PER_PAGE));


    function toggleInvoiceModal(): void {
        const newVal: boolean = !showInvoiceModal;
        setShowInvoiceModal(newVal);
        if (newVal === false) {
            setActiveInvoice(undefined);
        }
    }

    function handleShowInvoiceModal(invoice: SubscriptionInvoice): void {
        setActiveInvoice(invoice);
        setShowInvoiceModal(true);
    }

    function handleNextPage(): void {
        const newPage: number = activePage + 1;
        if (newPage <= pageCount) {
            setActivePage(newPage);
        }
    }

    function handlePreviousPage(): void {
        const newPage: number = activePage - 1;
        if (newPage > 0) {
            setActivePage(newPage);
        }
    }

    return (
        <>
            <div className="subscription__data-panel">
                <div className="data-panel__stripe-id">
                    <span className="stripe-id__title">{ "Stripe Subscription ID:" }</span>
                    { (loadingTestModeData) &&
                        <LinearProgress sx={ { margin: '10px 0', width: '75%' } } />
                    }
                    { (!loadingTestModeData) &&
                        <span>
                            { (isTestMode) &&
                                <Link to={ "https://dashboard.stripe.com/acct_1S676LIYyPSARt7l/test/subscriptions/" + activeSubscription.stripeId }
                                      target="_blank"
                                >{ activeSubscription.stripeId }
                                </Link>
                            }
                            { (!isTestMode) &&
                                <Link to={ "https://dashboard.stripe.com/acct_1S676LIYyPSARt7l/subscriptions/" + activeSubscription.stripeId }
                                      target="_blank"
                                >{ activeSubscription.stripeId }
                                </Link>
                            }
                        </span>
                    }
                </div>
                <div className="data-panel__stripe-data">
                    { (loadingData) &&
                        <div className="stripe-data__loader">
                            <span>{ "Loading Data..." }</span>
                            <CircularProgress size={30} />
                        </div>
                    }
                    { (!loadingData) &&
                        <>
                            { (loadingError !== '') &&
                                <div className="stripe-data__error">
                                    <span>{ loadingError }</span>
                                </div>
                            }

                            { (loadingError === '') &&
                                <>
                                    <div className="stripe-data__data">
                                        <div className="data__item">
                                            <span className="label">{ "Next Payment Date" }</span>
                                            <span>{ subscriptionData?.next_invoice_date }</span>
                                        </div>
                                        <div className="data__item extra">
                                            <span className="label">{ "Next Invoice" }</span>
                                            <div className="item__extra">
                                                <div className="extra__row">
                                                    <span>{ "Invoice Subtotal" }</span>
                                                    <span>{ convertCurrencyToSymbol(currencyCode) + '' + subscriptionData?.next_invoice_subtotal?.toFixed(2) }</span>
                                                </div>
                                                { (subscriptionData?.starting_balance < 0) &&
                                                    <div className="extra__row">
                                                        <span>{ "Invoice Credit" }</span>
                                                        <span>{ convertCurrencyToSymbol(currencyCode) + '' + subscriptionData?.starting_balance?.toFixed(2) }</span>
                                                    </div>
                                                }
                                                { (subscriptionData?.discount_amount > 0) &&
                                                    <div className="extra__row">
                                                        <span>
                                                            { "Coupon Discount" }
                                                            { (subscriptionData?.discount_percent > 0) &&
                                                                <span>{ ' (' + subscriptionData?.discount_percent + '% off)' }</span>
                                                            }
                                                        </span>
                                                        <span>{ '-' + convertCurrencyToSymbol(currencyCode) + '' + subscriptionData?.discount_amount?.toFixed(2) }</span>
                                                    </div>
                                                }
                                                <div className="extra__row">
                                                    <span>{ "Tax" }</span>
                                                    <span>{ convertCurrencyToSymbol(currencyCode) + '' + subscriptionData?.tax?.toFixed(2) }</span>
                                                </div>
                                                <div className="extra__row">
                                                    <span>{ "Invoice Amount Due" }</span>
                                                    <span>{ convertCurrencyToSymbol(currencyCode) + '' + subscriptionData?.next_invoice_amount_due?.toFixed(2) }</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="stripe-data__invoices">
                                        <span className="invoices__title" >{ "Subscription Invoices" }</span>
                                        { (isPending) &&
                                            <div className="invoices__loader">
                                                <span>{ "Loading Invoices" }</span>
                                                <CircularProgress size={30} />
                                            </div>
                                        }
                                        { (!isPending) &&
                                            <>
                                                { (error) &&
                                                    <div className="invoices__error">
                                                        <span>{ error.message }</span>
                                                    </div>
                                                }
                                                { (!error) &&
                                                    <div className="invoices__items">
                                                        <div className="items__header">
                                                            <span>{ "Id" }</span>
                                                            <span className="left">{ "Status" }</span>
                                                            <span className="left">{ "Date" }</span>
                                                            <span className="right">{ "View" }</span>
                                                        </div>
                                                        <div className="items__items">
                                                            { data?.map( (item: SubscriptionInvoice, index: number): JSX.Element => {
                                                                let dateString: string;
                                                                const date = new Date(item.createdAt);
                                                                let mVal: number = date.getMonth() + 1;
                                                                let month: string;
                                                                if (mVal < 10) {
                                                                    month = "0" + mVal;
                                                                } else {
                                                                    month = mVal.toString();
                                                                }
                                                                let dVal: number = date.getDate();
                                                                let day: string;
                                                                if (dVal < 10) {
                                                                    day = "0" + dVal;
                                                                } else {
                                                                    day = dVal.toString();
                                                                }
                                                                dateString = `${day}/${month}/${date.getFullYear()}`;
                                                                return (
                                                                    <div key={ "invoice_row_item_" + index }
                                                                         className="items__item">
                                                                        <span>{ item.id }</span>
                                                                        <span className={ "left status " + item.status }>
                                                                            { item.status }
                                                                        </span>
                                                                        <span className="left">{ dateString }</span>
                                                                        <Button type="button"
                                                                                onClick={ (): void => handleShowInvoiceModal(item) }
                                                                                variant="contained">
                                                                            <span>{ "View" }</span>
                                                                        </Button>
                                                                    </div>
                                                                );
                                                            } ) }
                                                        </div>
                                                        <div className="items__pagination">
                                                            <button type="button"
                                                                    aria-label="Previous page"
                                                                    disabled={ activePage <= 1 }
                                                                    onClick={ handlePreviousPage }>
                                                                <span>{ "<" }</span>
                                                            </button>
                                                            <span>{ activePage + ' / ' + pageCount }</span>
                                                            <button type="button"
                                                                    aria-label="Next page"
                                                                    disabled={ activePage >= pageCount }
                                                                    onClick={ handleNextPage }>
                                                                <span>{ ">" }</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                }
                                            </>
                                        }
                                    </div>
                                </>
                            }
                        </>
                    }
                </div>
            </div>
            <SubscriptionManagerActiveSubscriptionInvoiceModal open={ showInvoiceModal }
                                                               activeInvoice={ activeInvoice }
                                                               onClose={ toggleInvoiceModal } />
        </>
    );
}